import React from 'react';

import { type PlatformSettingsFields } from '../index';
import { RenderFields, type SectionWithFields } from '~/components/RenderFields';
import { Section } from '~/components/Section';
import { TransferApprovalConfiguration } from '~/lib/transfers/components/TransferApprovalConfiguration';

import { AdminApprovalConfiguration } from './AdminApprovalConfiguration';

interface Props {
  canConfigureTransferApprovals: boolean;
}

export const ApprovalRules: React.FC<Props> = ({ canConfigureTransferApprovals }) => {
  const sections = [
    {
      headline: 'Transfers',
      variant: 'isCompact',
      description: `Configures transfer types which require approval. Transfers may be approved by users with the “Approve Transfers” permission on their role.${
        !canConfigureTransferApprovals
          ? ' Contact Column support to configure transfer approvals for this platform.'
          : ''
      }`,
      fields: [{ content: <TransferApprovalConfiguration canConfigure={canConfigureTransferApprovals} /> }],
    },
    {
      headline: 'Platform Administration',
      variant: 'isCompact',
      description:
        'Configures whether administrative changes require approval. Changes may be approved by admins and users with the "User Management" permission on their role.',
      fields: [
        {
          id: 'requireUserChangeApproval',
          label: 'User Access Changes',
          tooltip: 'Requires approval for team invites, user role changes, and changes to custom role permissions.',
          children: ({ value, onChange }) => <AdminApprovalConfiguration value={value === true} onChange={onChange} />,
        },
      ],
    },
  ] satisfies SectionWithFields<PlatformSettingsFields, undefined>[];

  return (
    <Section headline="Approval Rules">
      <div className="flex flex-col gap-4">
        {sections.map(({ headline, description, fields }) => (
          <div
            key={headline}
            className="rounded-lg bg-gray-25 px-(--page-margin) pt-4 pb-4 shadow-[0_0_0_1px_var(--color-gray-150)] dark:bg-white/[0.02] dark:shadow-[0_0_0_1px_rgb(255_255_255/0.08)]"
          >
            <h3 className="m-0 pb-1 text-base leading-5 font-medium tracking-[-0.02em] text-foreground">{headline}</h3>
            <p className="m-0 pb-5 text-xs leading-[18px] text-muted-foreground">{description}</p>
            <RenderFields<PlatformSettingsFields> sections={[{ variant: 'isCompact', fields }]} />
          </div>
        ))}
      </div>
    </Section>
  );
};
